import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Card, CardHeader, CardBody, Button, Input, Label, Badge, Spinner, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Breadcrumbs } from '../../AbstractElements';
import { Home, MapPin, Truck, Mail, Phone, Edit2, Briefcase } from 'react-feather';
import axios from 'axios';
import { toast } from 'react-toastify';
import API_BASE_URL from '../../Config/api';
import authHeader from './authHeader';

const EMPTY = { name: '', location: '', country: '', shipping_address: '', email: '', phone: '' };

const MyStores = () => {
  const [stores, setStores] = useState([]);
  // One sign-in can cover several accounts (a chain keeps one per location);
  // each has its own email / phone / shipping address that its stores inherit.
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [formAccount, setFormAccount] = useState('');
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY);
  const [editName, setEditName] = useState(''); // store name shown in the modal title
  const [editAccount, setEditAccount] = useState(null); // owning account, shown read-only
  const [savingEdit, setSavingEdit] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/customer/stores`, authHeader());
      const list = res.data.accounts || [];
      setStores(res.data.stores || []);
      setAccounts(list);
      setFormAccount((prev) => prev || String(res.data.defaultAccountId || list[0]?.id || ''));
    } catch (e) {
      console.error('Failed to load stores', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const multiAccount = accounts.length > 1;
  const accountOf = (id) => accounts.find((a) => a.id === id) || {};
  // The account a new store would be created under, whose details it inherits.
  const defaults = accountOf(Number(formAccount));

  // Stores grouped under the account that owns them, so a chain sees its
  // locations separated by the entity each one bills through.
  const grouped = useMemo(() => {
    const byAccount = new Map();
    stores.forEach((s) => {
      if (!byAccount.has(s.customer_id)) byAccount.set(s.customer_id, []);
      byAccount.get(s.customer_id).push(s);
    });
    return accounts
      .filter((a) => byAccount.has(a.id))
      .map((a) => ({ account: a, stores: byAccount.get(a.id) }));
  }, [stores, accounts]);

  const addStore = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Store name is required'); return; }
    setSaving(true);
    try {
      await axios.post(
        `${API_BASE_URL}/api/customer/stores`,
        { ...form, customer_id: Number(formAccount) || undefined },
        authHeader()
      );
      toast.success('Store added');
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to add store');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (s) => {
    setEditId(s.id);
    setEditName(s.name || '');
    setEditAccount(accountOf(s.customer_id));
    setEditForm({
      name: s.name || '', location: s.location || '', country: s.country || '',
      shipping_address: s.shipping_address || '', email: s.email || '', phone: s.phone || '',
    });
  };

  const cancelEdit = () => { setEditId(null); setSavingEdit(false); };

  const setEdit = (k) => (e) => setEditForm({ ...editForm, [k]: e.target.value });

  const saveEdit = async () => {
    if (!editForm.name.trim()) { toast.error('Store name is required'); return; }
    setSavingEdit(true);
    try {
      await axios.put(`${API_BASE_URL}/api/customer/stores/${editId}`, editForm, authHeader());
      toast.success('Store updated');
      setEditId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to update store');
    } finally {
      setSavingEdit(false);
    }
  };

  // What actually ships / gets contacted for a store: its own value, else the
  // details of the account that owns it.
  const effEmail = (s) => s.email || accountOf(s.customer_id).email || '-';
  const effPhone = (s) => s.phone || accountOf(s.customer_id).phone || '-';
  const effShip = (s) => s.shipping_address || accountOf(s.customer_id).shipping_address || '-';

  // True when the store shows its account's value rather than one of its own.
  const inherited = (s, field) => !s[field] && !!accountOf(s.customer_id)[field];

  return (
    <Fragment>
      <Breadcrumbs parent="Dashboard" title="My Stores" />
      <Container fluid={true}>
        <Row>
          <Col lg="4">
            <Card>
              <CardHeader>
                <h4>Add a Store</h4>
                <span>Create a store to ship orders to a specific location.</span>
              </CardHeader>
              <CardBody>
                <form onSubmit={addStore}>
                  {multiAccount && (
                    <div className="mb-3">
                      <Label className="form-label">Account *</Label>
                      <Input type="select" value={formAccount} onChange={(e) => setFormAccount(e.target.value)}>
                        {accounts.map((a) => <option key={a.id} value={a.id}>{a.store_name}</option>)}
                      </Input>
                      <p className="text-muted f-12 mb-0 mt-1">The store is billed through this account.</p>
                    </div>
                  )}
                  <div className="mb-3">
                    <Label className="form-label">Store Name *</Label>
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Fara Boutique — Goa" />
                  </div>
                  <Row>
                    <Col xs="6">
                      <div className="mb-3">
                        <Label className="form-label">Location</Label>
                        <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
                      </div>
                    </Col>
                    <Col xs="6">
                      <div className="mb-3">
                        <Label className="form-label">Country</Label>
                        <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
                      </div>
                    </Col>
                  </Row>
                  <div className="mb-3">
                    <Label className="form-label">Shipping Address</Label>
                    <Input type="textarea" rows="2" value={form.shipping_address}
                      placeholder={defaults.shipping_address || ''}
                      onChange={(e) => setForm({ ...form, shipping_address: e.target.value })} />
                  </div>
                  <div className="mb-3">
                    <Label className="form-label">Email</Label>
                    <Input value={form.email}
                      placeholder={defaults.email ? `Default: ${defaults.email}` : ''}
                      onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  </div>
                  <div className="mb-3">
                    <Label className="form-label">Phone</Label>
                    <Input value={form.phone}
                      placeholder={defaults.phone ? `Default: ${defaults.phone}` : ''}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                  </div>
                  <p className="text-muted f-12 mb-3">Leave Email / Phone / Shipping Address blank to use your account defaults.</p>
                  <Button type="submit" color="primary" className="w-100" disabled={saving}>{saving ? 'Saving…' : 'Add Store'}</Button>
                </form>
              </CardBody>
            </Card>
          </Col>
          <Col lg="8">
            <Card>
              <CardHeader className="d-flex align-items-center justify-content-between">
                <div>
                  <h4>My Stores</h4>
                  <span>
                    {multiAccount
                      ? `Shipping destinations across the ${accounts.length} accounts linked to your sign-in.`
                      : 'Shipping destinations linked to your account.'}
                  </span>
                </div>
                {!loading && stores.length > 0 && (
                  <Badge color="primary">{stores.length} store{stores.length !== 1 ? 's' : ''}</Badge>
                )}
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="text-center py-5"><Spinner size="sm" /> <span className="text-muted ms-1">Loading your stores…</span></div>
                ) : stores.length === 0 ? (
                  <div className="rf-empty">
                    <div className="rf-empty-icon"><MapPin /></div>
                    <h6>No stores yet</h6>
                    <p>Add your first store using the form on the left. Stores let you ship orders to different locations.</p>
                  </div>
                ) : (
                  <div>
                    {grouped.map(({ account, stores: accountStores }) => (
                      <div key={account.id} className="mb-2">
                        {multiAccount && (
                          <div className="d-flex align-items-center gap-2 mb-2 pb-1 border-bottom">
                            <Briefcase size={14} className="text-muted" />
                            <span className="fw-semibold">{account.store_name}</span>
                            <span className="text-muted f-12">
                              {accountStores.length} store{accountStores.length !== 1 ? 's' : ''}
                              {account.vat_eori ? ` · VAT ${account.vat_eori}` : ''}
                            </span>
                          </div>
                        )}
                        {accountStores.map((s) => (
                          <div className="rf-entity-card" key={s.id}>
                            <div className="d-flex align-items-start justify-content-between gap-2 mb-3">
                              <div className="d-flex align-items-center gap-2">
                                <span className="rf-entity-avatar"><Home /></span>
                                <div>
                                  <div className="fw-semibold" style={{ fontSize: 15 }}>{s.name}</div>
                                  {(s.location || s.country) && (
                                    <div className="text-muted f-12 d-flex align-items-center gap-1">
                                      <MapPin size={12} /> {[s.location, s.country].filter(Boolean).join(', ')}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <Button color="light" size="sm" onClick={() => startEdit(s)}>
                                <Edit2 size={14} className="me-1" /> Edit
                              </Button>
                            </div>
                            <Row className="g-3">
                              <Col sm="12">
                                <div className="rf-detail-label"><Truck /> Shipping Address</div>
                                <div className="rf-detail-value" style={{ whiteSpace: 'pre-wrap' }}>
                                  {effShip(s)}
                                  {inherited(s, 'shipping_address') && <span className="rf-default-tag">default</span>}
                                </div>
                              </Col>
                              <Col sm="6">
                                <div className="rf-detail-label"><Mail /> Email</div>
                                <div className="rf-detail-value">
                                  {effEmail(s)}
                                  {inherited(s, 'email') && <span className="rf-default-tag">default</span>}
                                </div>
                              </Col>
                              <Col sm="6">
                                <div className="rf-detail-label"><Phone /> Phone</div>
                                <div className="rf-detail-value">
                                  {effPhone(s)}
                                  {inherited(s, 'phone') && <span className="rf-default-tag">default</span>}
                                </div>
                              </Col>
                            </Row>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal isOpen={editId !== null} toggle={cancelEdit} centered>
        <ModalHeader toggle={cancelEdit}>Edit Store{editName ? ` — ${editName}` : ''}</ModalHeader>
        <ModalBody>
          {multiAccount && editAccount?.store_name && (
            <p className="text-muted f-12 mb-2 d-flex align-items-center gap-1">
              <Briefcase size={12} /> Billed through <strong className="text-dark">{editAccount.store_name}</strong>
            </p>
          )}
          <Label className="form-label">Store Name *</Label>
          <Input className="mb-2" value={editForm.name} onChange={setEdit('name')} />
          <Row>
            <Col xs="6">
              <Label className="form-label">Location</Label>
              <Input className="mb-2" value={editForm.location} onChange={setEdit('location')} />
            </Col>
            <Col xs="6">
              <Label className="form-label">Country</Label>
              <Input className="mb-2" value={editForm.country} onChange={setEdit('country')} />
            </Col>
          </Row>
          <Label className="form-label">Shipping Address</Label>
          <Input type="textarea" rows="2" className="mb-2" value={editForm.shipping_address}
            placeholder={editAccount?.shipping_address || ''} onChange={setEdit('shipping_address')} />
          <Row>
            <Col md="6">
              <Label className="form-label">Email</Label>
              <Input className="mb-2" value={editForm.email}
                placeholder={editAccount?.email ? `Default: ${editAccount.email}` : ''} onChange={setEdit('email')} />
            </Col>
            <Col md="6">
              <Label className="form-label">Phone</Label>
              <Input className="mb-2" value={editForm.phone}
                placeholder={editAccount?.phone ? `Default: ${editAccount.phone}` : ''} onChange={setEdit('phone')} />
            </Col>
          </Row>
          <p className="text-muted f-12 mb-0">Leave Email / Phone / Shipping Address blank to use your account defaults.</p>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={cancelEdit} disabled={savingEdit}>Cancel</Button>
          <Button color="primary" onClick={saveEdit} disabled={savingEdit}>{savingEdit ? 'Saving...' : 'Save Changes'}</Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default MyStores;
